import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "../styles/profile.css";

const API_BASE_URL = "https://growrise.shop";

const PROFILE_DEFAULTS = {
  fullName: "",
  email: "",
  phone: "",
  country: "",
  city: "",
  dateOfBirth: "",
};

export default function Profile() {
  const [profile, setProfile] = useState(PROFILE_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [startingKyc, setStartingKyc] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [kycStatus, setKycStatus] = useState("unverified");
  const [isLocked, setIsLocked] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const [idFrontFile, setIdFrontFile] = useState(null);
  const [idBackFile, setIdBackFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);

  const [idFrontPreview, setIdFrontPreview] = useState("");
  const [idBackPreview, setIdBackPreview] = useState("");
  const [selfiePreview, setSelfiePreview] = useState("");

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const getAuthHeaders = (includeJson = false) => {
    const token = getToken();
    const headers = {};

    if (includeJson) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const normalizeStatus = (status) => {
    return String(status || "unverified").toLowerCase();
  };

  const formatKycStatus = (status) => {
    const value = normalizeStatus(status);

    if (value === "verified") return "Verified";
    if (value === "pending") return "Pending";
    if (value === "rejected") return "Rejected";
    return "Unverified";
  };

  const checkLockStatus = (data) => {
    return Boolean(
      String(data.phone || "").trim() &&
        String(data.country || "").trim() &&
        String(data.city || "").trim() &&
        String(data.dateOfBirth || "").trim()
    );
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const clearAuthStorage = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("naxoraToken");
    localStorage.removeItem("naxoraAuth");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("naxoraCurrentUser");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("user");
  };

  const handleUnauthorized = async () => {
    await Swal.fire({
      icon: "error",
      title: "Unauthorized",
      text: "Please login again",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    clearAuthStorage();
    window.location.replace("/login");
  };

  const persistUserLocally = (userData) => {
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("currentUser", JSON.stringify(userData));
    localStorage.setItem("naxoraCurrentUser", JSON.stringify(userData));
  };

  const fetchProfileAndKyc = async () => {
    try {
      setLoadingProfile(true);

      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/kyc/me`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to load profile");
      }

      const profileData = data.profile || {};
      const kycData = data.kyc || null;

      const storedUser = JSON.parse(localStorage.getItem("user")) || {};
      const storedCurrentUser =
        JSON.parse(localStorage.getItem("currentUser")) || {};
      const storedNaxoraUser =
        JSON.parse(localStorage.getItem("naxoraCurrentUser")) || {};

      const mergedUser = {
        ...storedUser,
        ...storedCurrentUser,
        ...storedNaxoraUser,
        ...profileData,
      };

      const nextProfile = {
        fullName:
          profileData.fullName || profileData.name || mergedUser.name || "",
        email: profileData.email || mergedUser.email || "",
        phone: profileData.phone || mergedUser.phone || "",
        country: profileData.country || mergedUser.country || "",
        city: profileData.city || mergedUser.city || "",
        dateOfBirth: profileData.dateOfBirth || mergedUser.dateOfBirth || "",
      };

      const nextStatus =
        profileData.kycStatus || mergedUser.kycStatus || "unverified";

      setProfile(nextProfile);
      setKycStatus(nextStatus);
      setRejectionReason(kycData?.rejectionReason || "");

      setIdFrontPreview(
        (kycData && kycData.idFrontUrl) || mergedUser.idFrontPreview || ""
      );
      setIdBackPreview(
        (kycData && kycData.idBackUrl) || mergedUser.idBackPreview || ""
      );
      setSelfiePreview(
        (kycData && kycData.selfieUrl) || mergedUser.selfiePreview || ""
      );

      setIsLocked(checkLockStatus(nextProfile));

      persistUserLocally({
        ...mergedUser,
        ...nextProfile,
        name: nextProfile.fullName,
        fullName: nextProfile.fullName,
        email: nextProfile.email,
        phone: nextProfile.phone,
        country: nextProfile.country,
        city: nextProfile.city,
        dateOfBirth: nextProfile.dateOfBirth,
        kycStatus: nextStatus,
        rejectionReason: kycData?.rejectionReason || "",
        idFrontPreview:
          (kycData && kycData.idFrontUrl) || mergedUser.idFrontPreview || "",
        idBackPreview:
          (kycData && kycData.idBackUrl) || mergedUser.idBackPreview || "",
        selfiePreview:
          (kycData && kycData.selfieUrl) || mergedUser.selfiePreview || "",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to load profile",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    fetchProfileAndKyc();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageSelect = (e, type) => {
    const file = e.target.files && e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire({
        icon: "warning",
        title: "Invalid File",
        text: "Please select an image file",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    if (type === "front") {
      setIdFrontFile(file);
      setIdFrontPreview(previewUrl);
    }

    if (type === "back") {
      setIdBackFile(file);
      setIdBackPreview(previewUrl);
    }

    if (type === "selfie") {
      setSelfieFile(file);
      setSelfiePreview(previewUrl);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const existingUser = JSON.parse(localStorage.getItem("user")) || {};

      const updatedUser = {
        ...existingUser,
        ...profile,
        name: profile.fullName,
        fullName: profile.fullName,
        email: profile.email,
        phone: profile.phone,
        country: profile.country,
        city: profile.city,
        dateOfBirth: profile.dateOfBirth,
        kycStatus,
        rejectionReason,
        idFrontPreview,
        idBackPreview,
        selfiePreview,
      };

      persistUserLocally(updatedUser);

      if (checkLockStatus(profile)) {
        setIsLocked(true);
      }

      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Profile details saved successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Unable to save profile",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStartKyc = async () => {
    try {
      if (!profile.fullName.trim()) {
        throw new Error("Full name is required");
      }

      if (!profile.email.trim()) {
        throw new Error("Email is required");
      }

      if (!profile.phone.trim()) {
        throw new Error("Phone is required");
      }

      if (!profile.country.trim()) {
        throw new Error("Country is required");
      }

      if (!profile.city.trim()) {
        throw new Error("City is required");
      }

      if (!profile.dateOfBirth.trim()) {
        throw new Error("Date of birth is required");
      }

      if (!idFrontFile && !idFrontPreview) {
        throw new Error("National ID front image is required");
      }

      if (!idBackFile && !idBackPreview) {
        throw new Error("National ID back image is required");
      }

      if (!selfieFile && !selfiePreview) {
        throw new Error("Selfie / face verification image is required");
      }

      const currentStatus = normalizeStatus(kycStatus);

      if (currentStatus === "pending") {
        throw new Error("Your KYC is already pending review");
      }

      if (currentStatus === "verified") {
        throw new Error("Your KYC is already verified");
      }

      setStartingKyc(true);

      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      const idFront =
        idFrontFile instanceof File
          ? await fileToBase64(idFrontFile)
          : idFrontPreview;

      const idBack =
        idBackFile instanceof File
          ? await fileToBase64(idBackFile)
          : idBackPreview;

      const selfie =
        selfieFile instanceof File
          ? await fileToBase64(selfieFile)
          : selfiePreview;

      const response = await fetch(`${API_BASE_URL}/api/kyc/submit`, {
        method: "POST",
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          fullName: profile.fullName.trim(),
          email: profile.email.trim(),
          phone: profile.phone.trim(),
          country: profile.country.trim(),
          city: profile.city.trim(),
          dateOfBirth: profile.dateOfBirth,
          idFront,
          idBack,
          selfie,
        }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Unable to submit KYC");
      }

      setKycStatus("pending");
      setRejectionReason("");
      setIsLocked(true);

      const updatedUser = {
        ...(JSON.parse(localStorage.getItem("user")) || {}),
        ...profile,
        name: profile.fullName,
        fullName: profile.fullName,
        email: profile.email,
        phone: profile.phone,
        country: profile.country,
        city: profile.city,
        dateOfBirth: profile.dateOfBirth,
        kycStatus: "pending",
        rejectionReason: "",
        idFrontPreview: idFront,
        idBackPreview: idBack,
        selfiePreview: selfie,
      };

      persistUserLocally(updatedUser);

      if (data.kyc) {
        setIdFrontPreview(data.kyc.idFrontUrl || idFrontPreview);
        setIdBackPreview(data.kyc.idBackUrl || idBackPreview);
        setSelfiePreview(data.kyc.selfieUrl || selfiePreview);
      }

      setIdFrontFile(null);
      setIdBackFile(null);
      setSelfieFile(null);

      Swal.fire({
        icon: "success",
        title: "KYC Submitted",
        text: "Your verification request has been submitted successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to submit KYC",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setStartingKyc(false);
    }
  };

  const currentStatus = normalizeStatus(kycStatus);
  const showUploads =
    currentStatus === "unverified" || currentStatus === "rejected";
  const showRetryNote = currentStatus === "rejected";
  const showPendingNote = currentStatus === "pending";
  const showVerifiedNote = currentStatus === "verified";

  const submitButtonText =
    currentStatus === "rejected"
      ? startingKyc
        ? "Retrying..."
        : "Retry KYC"
      : startingKyc
      ? "Submitting..."
      : "Submit KYC";

  if (loadingProfile) {
    return (
      <div className="profile-page">
        <div className="profile-wrapper">
          <div className="profile-header">
            <h1>Profile</h1>
          </div>

          <div className="profile-loading-state">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-wrapper">
        <div className="profile-header">
          <h1>Profile</h1>
        </div>

        <div className="profile-grid">
          <div className="profile-card profile-left-card">
            <div className="profile-card-head">
              <h2>Personal Information</h2>
            </div>

            <form className="profile-form" onSubmit={handleSaveProfile}>
              <div className="profile-form-grid">
                <div className="profile-form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={profile.fullName}
                    readOnly
                  />
                </div>

                <div className="profile-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={profile.email}
                    readOnly
                  />
                </div>

                <div className="profile-form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={profile.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    disabled={isLocked}
                  />
                </div>

                <div className="profile-form-group">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={profile.dateOfBirth}
                    onChange={handleChange}
                    disabled={isLocked}
                  />
                </div>

                <div className="profile-form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={profile.country}
                    onChange={handleChange}
                    placeholder="Enter country"
                    disabled={isLocked}
                  />
                </div>

                <div className="profile-form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={profile.city}
                    onChange={handleChange}
                    placeholder="Enter city"
                    disabled={isLocked}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="profile-save-btn"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </form>
          </div>

          <div className="profile-card profile-right-card">
            <div className="profile-card-head">
              <h2>KYC Verification</h2>
            </div>

            <div className="kyc-status-box">
              <span className="kyc-status-label">Status</span>
              <strong className={`kyc-status-value ${currentStatus}`}>
                {formatKycStatus(currentStatus)}
              </strong>
            </div>

            {showRetryNote && rejectionReason ? (
              <div className="kyc-note-box">
                <p>Reason: {rejectionReason}</p>
              </div>
            ) : null}

            {showPendingNote ? (
              <div className="kyc-note-box">
                <p>Your KYC is pending admin review.</p>
              </div>
            ) : null}

            {showVerifiedNote ? (
              <div className="kyc-note-box">
                <p>Your KYC has been verified successfully.</p>
              </div>
            ) : null}

            <div className="kyc-feature-list">
              <div className="kyc-feature-item">
                <span>Document Check</span>
                <strong>Enabled</strong>
              </div>

              <div className="kyc-feature-item">
                <span>Face Match</span>
                <strong>Enabled</strong>
              </div>

              <div className="kyc-feature-item">
                <span>Liveness Check</span>
                <strong>Enabled</strong>
              </div>

              <div className="kyc-feature-item">
                <span>Provider</span>
                <strong>Growrise KYC</strong>
              </div>
            </div>

            {showUploads ? (
              <>
                <div className="kyc-note-box">
                  <p>
                    Upload national ID front, back, and selfie image for manual
                    admin verification.
                  </p>
                </div>

                <div className="kyc-upload-grid">
                  <div className="kyc-upload-card">
                    <label className="kyc-upload-label">ID Card Front</label>
                    <label className="kyc-upload-btn">
                      Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageSelect(e, "front")}
                        hidden
                      />
                    </label>
                    {idFrontPreview ? (
                      <img
                        src={idFrontPreview}
                        alt="ID Front"
                        className="kyc-preview-image"
                      />
                    ) : (
                      <div className="kyc-preview-placeholder">
                        No image selected
                      </div>
                    )}
                  </div>

                  <div className="kyc-upload-card">
                    <label className="kyc-upload-label">ID Card Back</label>
                    <label className="kyc-upload-btn">
                      Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageSelect(e, "back")}
                        hidden
                      />
                    </label>
                    {idBackPreview ? (
                      <img
                        src={idBackPreview}
                        alt="ID Back"
                        className="kyc-preview-image"
                      />
                    ) : (
                      <div className="kyc-preview-placeholder">
                        No image selected
                      </div>
                    )}
                  </div>

                  <div className="kyc-upload-card">
                    <label className="kyc-upload-label">Face Verification</label>
                    <label className="kyc-upload-btn">
                      Open Camera
                      <input
                        type="file"
                        accept="image/*"
                        capture="user"
                        onChange={(e) => handleImageSelect(e, "selfie")}
                        hidden
                      />
                    </label>
                    {selfiePreview ? (
                      <img
                        src={selfiePreview}
                        alt="Face Verification"
                        className="kyc-preview-image"
                      />
                    ) : (
                      <div className="kyc-preview-placeholder">
                        No image selected
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="kyc-start-btn"
                  onClick={handleStartKyc}
                  disabled={startingKyc}
                >
                  {submitButtonText}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}